import React from 'react';
import Moment from 'moment';
import * as yup from 'yup';
import {Formik} from 'formik';
import {useIntl} from 'react-intl';
import filterCCTypesByProvider from '../../config/cc.types';
import {
  getIsStillWorkingHourOrPolicyStartDateNotBlocked,
  producingPolicy,
  verifyPayment,
} from '../../utils/apiHandlers';
import {getSelectedOffer} from '../../utils/persistOfferDetails';
import {fireGtmEvent} from '../../utils/marketing.utils';
import {hebrewCharsRegExp, validateForIsraeliID} from '../../utils/validationHelpers';
import {scrollToFirstError} from '../../utils/scrollToFirstError';
import {agentDeveloperUtils} from 'wobi-web-common';
import PaymentCheckout from '../../components/PaymentCheckout';
import QuoteExpiredModal from '../../components/modals/QuoteExpired';
import './Payment.css';

const DEBIT_CARD_IS_NOT_ALLOWED_CODE = 999;

const Payment = React.memo(({className, offer, initialTabs, completeDetailsValues, onBackToOffers, submitAct}) => {
  const intl = useIntl();
  const messages = (id) => intl.formatMessage({id});
  const [submitError, setSubmitError] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isQuoteExpired, setIsQuoteExpired] = React.useState(false);

  React.useEffect(() => {
    window.onbeforeunload = () => true;
    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  const selectedOffer = getSelectedOffer();
  const filteredCCTypes = filterCCTypesByProvider(selectedOffer.provider);
  const validationObject = {
    ccType: yup.string().typeError().required(messages('validation.required')),
    ccNumber: yup.string().typeError().required(messages('validation.required'))
      .when('ccType', {
        is: 'American-Express',
        then: yup.string()
          .min(15, messages('validation.payment.amexLength'))
          .max(15, messages('validation.payment.amexLength'))
          .matches(/^\d{15}$/, messages('validation.payment.digitsOnly')),
        otherwise: yup.string()
          .when('ccType', {
            is: 'Diners',
            then: yup.string()
              .matches(/^\d{14}$|^\d{16}$/, messages('validation.payment.dinersLength'))
              .test('valid-length', messages('validation.payment.dinersLength'),
                value => value && (value.length === 14 || value.length === 16)),

            otherwise: yup.string()
              .matches(/^\d{14}$|^\d{16}$/, messages('validation.payment.cardNumberLength'))
          }),
      }),
    cvv: yup.string().typeError().required(messages('validation.required'))
      .min(3, messages('validation.payment.cvvMin'))
      .max(4, messages('validation.payment.cvvMax'))
      .matches(/^\d{3,4}$/, messages('validation.payment.digitsOnly')),
    ccExpiration: yup.string().typeError(messages('validation.payment.expirationFormat')).required(messages('validation.required'))
      .max(5, messages('validation.payment.expirationFormat'))
      .matches(/(\d{2})\/(\d{2})/, messages('validation.payment.expirationFormat'))
      .test('cc', messages('validation.payment.expirationInvalid'), (value) => {
        if (value) {
          const [month, year] = value.split('/');
          if (year && month) {
            const now = Moment();
            const end = Moment(`${month}/01/${year}`);
            if (end.diff(now, 'days') <= 0) {
              return false;
            }
          }
          return true;
        }
      }),
    ccOwnerName: yup.string()
      .test('fullNameHebrewOnly', messages('validation.payment.ownerNameHebrew'), (value) => {
        const arrayFromInput = value ? value.split(' ') : [];
        let result = true;
        if (arrayFromInput.length < 2) {
          result = false;
        }

        // make sure every word is in hebrew and not contain or equal empty string or spaces and not . or , in regex
        if (arrayFromInput.some(word => word === '' || word === ' ' || word === '.' || word === ',')) {
          result = false;
        }
        if (arrayFromInput.some(word => /^\d+$/.test(word))) {
          result = false;
        }
        if (arrayFromInput.some(word => /^[A-Za-z]+$/.test(word))) {
          result = false;
        }
        if (arrayFromInput.some(word => /^[\dA-Za-z]+$/.test(word))) {
          result = false;
        }
        if (!result) {
          return false;
        }
        const isEveryWordInHebrew = arrayFromInput
          .map(word => hebrewCharsRegExp.test(word))
          .every(word => word === true);
        return isEveryWordInHebrew;
      })
      .required(messages('validation.required')),
    ccOwnerId: validateForIsraeliID(),
  };

  const handleChangeCcType = (event, setFieldValue) => {
    setFieldValue('ccType', event.target.value);
  };

  const handleQuoteExpired = () => {
    setIsSubmitting(false);
    window.onbeforeunload = null;
    setIsQuoteExpired(true);
  };

  const handleQuoteExpiredConfirm = () => {
    sessionStorage.removeItem('travel.offersCallId');
    window.location.href = '/details';
  };

  const handleSubmit = async (values, validateForm, setFieldTouched) => {
    setIsSubmitting(true);
    setSubmitError('');
    const errors = await validateForm();
    const offersCallId = JSON.parse(sessionStorage.getItem('travel.offersCallId'));

    if (Object.keys(errors).length > 0) {
      Object.keys(validationObject).forEach(field => setFieldTouched(field, true));
      setIsSubmitting(false);
      scrollToFirstError(errors);
      return;
    }

    if (initialTabs.policyStartDate && Moment(initialTabs.policyStartDate).isBefore(Moment(), 'day')) {
      handleQuoteExpired();
      return;
    }

    try {
      const orderId = Moment().format('YYMMDDmmHHDDMMss');
      const selectedOffer = getSelectedOffer();
      const agentDeveloperName = agentDeveloperUtils.getAgentDeveloperName;
      const paymentResp = await verifyPayment({
        ...values,
        offersCallId,
        orderId,
        agentDeveloperName,
      });
      if (paymentResp?.statusCode === DEBIT_CARD_IS_NOT_ALLOWED_CODE) {
        setSubmitError(messages('validation.payment_debit_card_not_allowed'));
        return;
      }
      if (!paymentResp?.status) {
        setSubmitError(messages('validation.payment_submit_error'));
        return;
      }
      if (selectedOffer.isAutoProduce) {
        if (selectedOffer.provider.toUpperCase().includes('CLAL')) {
          await getIsStillWorkingHourOrPolicyStartDateNotBlocked(initialTabs.policyStartDate);
        }
        const policyResp = await producingPolicy({
          ...values,
          ccHolder: `${completeDetailsValues?.firstNameEng || ''} ${completeDetailsValues?.lastNameEng || ''}`.trim(),
          provider: selectedOffer.provider,
          offersCallId,
          orderId,
          agentDeveloperName,
        });
        if (policyResp?.error === 'QUOTE_EXPIRED') {
          handleQuoteExpired();
          return;
        }
        if (policyResp?.message === 'Error getting paymentToken') {
          setSubmitError(messages('validation.payment_submit_error'));
          return;
        }
        if (!policyResp || !Object.prototype.hasOwnProperty.call(policyResp, 'policyID') && !policyResp.ignoreError) {
          setSubmitError(messages('payment.policy_produce_error'));
          return;
        }
      }
      sessionStorage.setItem('orderId', orderId);
      sessionStorage.setItem('travel.payment', JSON.stringify({
        ccNumber: values.ccNumber.slice(Math.max(0, values.ccNumber.length - 4)),
      }));
      fireGtmEvent('confirmTravelOrder');
      submitAct();
    } catch (error) {
      setSubmitError(messages('validation.payment_submit_error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={className} data-testid='payment-container'>
      <QuoteExpiredModal open={isQuoteExpired} onConfirm={handleQuoteExpiredConfirm} />
      <Formik
        validationSchema={yup.object().shape(validationObject)}
        enableReinitialize
        initialValues={{
          ccType: '',
          ccNumber: '',
          cvv: '',
          ccExpiration: '',
          ccOwnerName: '',
          ccOwnerId: '',
        }}
        onSubmit={handleSubmit}
      >
        {({values, errors, touched, setFieldValue, validateForm, setFieldTouched, handleChange}) => (
          <PaymentCheckout
            errors={errors}
            filteredCCTypes={filteredCCTypes}
            handleChange={handleChange}
            initialTabs={initialTabs}
            isSubmitting={isSubmitting}
            offer={offer}
            onBackToOffers={onBackToOffers}
            onCardTypeChange={(event) => handleChangeCcType(event, setFieldValue)}
            onSubmit={() => handleSubmit(values, validateForm, setFieldTouched)}
            setFieldValue={setFieldValue}
            submitError={submitError}
            touched={touched}
            values={values}
          />
        )}
      </Formik>
    </div>
  );
});

export default Payment;
